import { rankingData, carouselData, indexData, type RankingItem, type CarouselItem, type IndexItem } from '@/mocks/homeData';

function SectionHeading({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="text-center mb-12">
      <h2 className="text-2xl md:text-3xl font-black text-white">{title}</h2>
      {subtitle && <p className="text-surface-400 text-sm mt-3">{subtitle}</p>}
    </div>
  );
}

// ─── #ranking 热搜吃瓜榜 ───
function RankingSection() {
  return (
    <section id="ranking" className="py-16 md:py-20 bg-surface-950">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionHeading title="热搜吃瓜榜" subtitle="实时热度追踪 · 每分钟更新排名" />
        <div className="max-w-3xl mx-auto space-y-3">
          {rankingData.map((item: RankingItem, i: number) => (
            <div
              key={i}
              className="flex items-center gap-4 p-4 rounded-xl bg-surface-900/60 border border-surface-800/60 hover:border-surface-700/60 transition-all duration-200 cursor-pointer"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <span className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-black flex-shrink-0 ${
                i === 0 ? 'bg-brand-500 text-white' :
                i === 1 ? 'bg-warm-500 text-surface-950' :
                i === 2 ? 'bg-warm-600 text-white' :
                'bg-surface-800 text-surface-400'
              }`}>
                {item.rank}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{item.title}</p>
              </div>
              <span className="text-xs px-2.5 py-1 rounded-full bg-brand-500/15 text-brand-400 border border-brand-500/20 whitespace-nowrap">
                {item.tag}
              </span>
              <span className="text-surface-400 text-xs whitespace-nowrap flex items-center gap-1">
                {item.trend === 'up' && <i className="ri-arrow-up-line text-brand-400"></i>}
                {item.trend === 'down' && <i className="ri-arrow-down-line text-surface-500"></i>}
                {item.trend === 'new' && <span className="text-warm-400 font-semibold text-xs">NEW</span>}
                {item.trend === 'stable' && <i className="ri-subtract-line text-surface-500"></i>}
                {item.heat}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── #app 每日大赛APP ───
function AppSection() {
  return (
    <section id="app" className="py-16 md:py-20 bg-surface-900">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-brand-600/30 via-surface-900 to-surface-900 border border-surface-800/60">
          <div className="flex flex-col lg:flex-row items-center gap-8 p-8 md:p-12">
            <div className="flex-1 text-center lg:text-left">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-500/20 text-brand-400 text-xs font-semibold mb-4">
                <i className="ri-download-2-line"></i> 官方下载
              </span>
              <h2 className="text-2xl md:text-3xl font-black text-white">每日大赛APP</h2>
              <p className="text-surface-300 text-sm md:text-base mt-3 max-w-md">
                支持 iOS / Android 双平台，与网页版实时同步内容。随时随地刷吃瓜、追热点，最新爆料推送比闹钟还准时。官方入口安全下载，无需担心恶意软件。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3 mt-6">
                <button className="w-full sm:w-auto px-7 py-3 bg-brand-500 hover:bg-brand-600 text-white text-sm font-semibold rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                  <i className="ri-apple-line text-lg"></i> App Store 下载
                </button>
                <button className="w-full sm:w-auto px-7 py-3 bg-surface-800 hover:bg-surface-700 text-white text-sm font-semibold rounded-lg border border-surface-600/50 transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                  <i className="ri-android-line text-lg"></i> Android 下载
                </button>
              </div>
              <p className="text-surface-500 text-xs mt-4">
                当前版本 v3.8.2 · 最近更新：2026年7月28日 · 累计下载量超 500万+
              </p>
            </div>
            <div className="flex-shrink-0 w-48 h-48 md:w-56 md:h-56 rounded-2xl overflow-hidden border-2 border-surface-700/40">
              <img
                src="https://readdy.ai/api/search-image?query=QR%20code%20design%20with%20dark%20theme%20and%20warm%20red%20accents%2C%20minimalist%20geometric%20pattern%2C%20modern%20app%20download%20visual%2C%20clean%20dark%20background%20with%20subtle%20gradient%2C%20sleek%20tech%20aesthetic&width=400&height=400&seq=app-qr-code&orientation=squarish"
                alt="每日大赛APP下载二维码"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── #carousel 焦点专题 (全部可见, 不轮播) ───
function CarouselSection() {
  return (
    <section id="carousel" className="py-16 md:py-20 bg-surface-950">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionHeading title="焦点专题" subtitle="编辑精选 · 深度追踪报道" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {carouselData.map((item: CarouselItem) => (
            <div key={item.id} className="group rounded-xl overflow-hidden bg-surface-900 border border-surface-800/60 hover:border-brand-500/30 transition-all duration-300 cursor-pointer">
              <div className="relative h-48 overflow-hidden">
                <img
                  src={item.imageUrl}
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 left-3">
                  <span className="px-2.5 py-1 text-xs font-semibold bg-brand-500 text-white rounded-md">
                    {item.tag}
                  </span>
                </div>
              </div>
              <div className="p-5">
                <h3 className="text-white font-bold text-base group-hover:text-brand-400 transition-colors duration-200 line-clamp-1">{item.title}</h3>
                <p className="text-surface-400 text-sm mt-2 line-clamp-2">{item.summary}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── #index 吃瓜专题索引 ───
function IndexSection() {
  return (
    <section id="index" className="py-16 md:py-20 bg-surface-900">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionHeading title="吃瓜专题索引" subtitle="分类浏览 · 各取所需" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-4">
          {indexData.map((item: IndexItem) => (
            <div key={item.id} className="group flex items-center gap-4 p-5 rounded-xl bg-surface-800/60 border border-surface-700/30 hover:border-brand-500/20 hover:bg-surface-800 transition-all duration-200 cursor-pointer">
              <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-brand-500/15 text-brand-400 flex-shrink-0 group-hover:bg-brand-500 group-hover:text-white transition-all duration-200">
                <i className={`${item.icon} text-lg`}></i>
              </span>
              <div className="min-w-0">
                <p className="text-white text-sm font-semibold">{item.title}</p>
                <p className="text-surface-400 text-xs mt-0.5">{item.count}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── #contact 投稿爆料 ───
function ContactSection() {
  return (
    <section id="contact" className="py-16 md:py-20 bg-surface-950">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="max-w-2xl mx-auto text-center">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-warm-500/15 text-warm-400 text-xs font-semibold mb-4">
            <i className="ri-mail-send-line"></i> 欢迎来稿
          </span>
          <h2 className="text-2xl md:text-3xl font-black text-white">投稿爆料</h2>
          <p className="text-surface-300 text-sm md:text-base mt-4 leading-relaxed">
            你有独家爆料？发现不为人知的圈内真相？每日大赛欢迎广大网友提供真实可靠的娱乐线索。所有投稿信息严格保密来源，经核实有价值的内容将获得优先展示和丰厚奖励。
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
            <div className="flex items-center gap-3 px-6 py-3 rounded-xl bg-surface-900 border border-surface-800/60">
              <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-brand-500/15 text-brand-400">
                <i className="ri-mail-line text-lg"></i>
              </span>
              <div className="text-left">
                <p className="text-surface-400 text-xs">投稿邮箱</p>
                <p className="text-white text-sm font-medium">tougao@meiridasai.com</p>
              </div>
            </div>
            <div className="flex items-center gap-3 px-6 py-3 rounded-xl bg-surface-900 border border-surface-800/60">
              <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-warm-500/15 text-warm-400">
                <i className="ri-wechat-line text-lg"></i>
              </span>
              <div className="text-left">
                <p className="text-surface-400 text-xs">微信客服</p>
                <p className="text-white text-sm font-medium">meiridasai_kf</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── #about 关于每日大赛 ───
function AboutSection() {
  return (
    <section id="about" className="py-16 md:py-20 bg-surface-900">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="max-w-3xl mx-auto">
          <SectionHeading title="关于每日大赛" />
          <div className="space-y-4 text-surface-300 text-sm md:text-base leading-relaxed">
            <p>
              每日大赛官网创建于2023年，是专注于娱乐圈热点追踪与深度解读的资讯聚合平台。我们致力于为用户提供一站式的娱乐资讯入口，覆盖明星动态、综艺翻车、片场爆料、直播事故、红毯名场面等全品类吃瓜内容。
            </p>
            <p>
              平台每日早中晚三次固定内容刷新，重大突发事件15分钟内上线快讯。编辑部由资深娱乐记者和行业观察者组成，每条内容均经过多重信源交叉验证，标注可信等级，让你吃得放心、看得过瘾。
            </p>
            <p>
              每日大赛已推出网页版、iOS版、Android版三大入口，数据实时同步，随时随地掌握最新吃瓜动态。我们坚持内容为王、用户至上，努力做全网最靠谱的吃瓜指南。
            </p>
          </div>
          <div className="grid grid-cols-3 gap-6 mt-10">
            {[
              { num: '500万+', label: '累计用户' },
              { num: '3.6万+', label: '累计内容' },
              { num: '98.7%', label: '用户好评率' },
            ].map((stat) => (
              <div key={stat.label} className="text-center p-5 rounded-xl bg-surface-800/60 border border-surface-700/30">
                <p className="text-2xl md:text-3xl font-black text-brand-400">{stat.num}</p>
                <p className="text-surface-400 text-xs mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function SectionsGroup1() {
  return (
    <>
      <RankingSection />
      <AppSection />
      <CarouselSection />
      <IndexSection />
      <ContactSection />
      <AboutSection />
    </>
  );
}